import { AppUiRecordDataService } from '../app-ui/app-ui-record.data.service';
import {
  DB_KEY_PREFIXES,
  PREFIX_SEARCH_ENDKEY_UNICODE,
} from '../../config/pouch-db.config';
import {
  DiskService,
  OFFLINE_DB_LINKED_ENTITY_NAME,
} from '../../services/disk/disk.service';
import { AppUiScreenDataService } from '../app-ui/app-ui-screen.data.service';
import { AuthenticationService } from '../../services/authentication.service';
import { DeliveryScheduleDataService } from '../delivery-schedule/delivery-schedule.data.service';
import { DeviceService } from '../../services/device/device.service';
import { DynamicsClientService } from '../dynamics-client/dynamics-client.service';
import { FeatureActionsMap } from '../../classes/authentication/user.class';
import { Injectable } from '@angular/core';
import { PricingDataService } from '../pricing/pricing.data.service';
import { filter } from 'rxjs';
import {
  FORMATTED,
  formattedText,
  idOf,
  isGuid,
} from '@omni/utility/dynamics-row.util';
import { SalesOrderState } from '@omni/enums/order-capture/sales-order.enum';
import {
  CURRENCY_ALIAS,
  CURRENCY_LINK,
} from '@omni/services/order-capture/order-currency';
import {
  addCondition,
  addFilter,
  addToRootEntity,
  selectAll,
  setTop,
  withoutTokens,
} from '@omni/services/app-ui/app-ui-fetch';

/** The order capture screen, pinned so the server never picks another salesorder layout. */
export const ORDER_SCREEN_KEY = 'salesorder.screen.ordermgmt';
export const ORDER_SCREEN_ENTITY = 'salesorder';

/** How long a successful refresh satisfies the next order screen that opens. */
export const SCREEN_REFRESH_INTERVAL_MS = 5 * 60 * 1000;

const ADDRESS_LINK = 'indskr_indskr_customeraddress_v2';
/** The account sync's store of every account's addresses: one document, or chunks and their meta. */
const ADDRESS_STORE_KEY =
  DB_KEY_PREFIXES.ACCOUNT_LINKED_ENTITY +
  OFFLINE_DB_LINKED_ENTITY_NAME.ADDRESSES;
/** How long a cached last order may miss another device's or rep's new order; this device's own drop it at once. */
const LAST_ORDER_TTL_MS = 5 * 60 * 1000;
/** A price list's limits on an order's discount; either may be absent. */
export interface PriceListCeiling {
  /** `indskr_maxdiscountpercentage`, applied to each line. */
  percentage?: number;
  /** `indskr_maxdiscountamount`, the most the whole order may take off, in the list's currency. */
  amount?: number;
}

/** A column a price-list rule may name: anything else is not read into a query. */
const COLUMN_NAME = /^[a-z_][a-z0-9_]*$/i;
/** New Order, then the screen it opens, read the same account's rule columns: one read serves both. */
const ACCOUNT_READ_REUSE_MS = 30_000;
/** How many of the rep's open orders a pull reads; drafts beyond it are left to the next pull. */
export const PULL_LIMIT = 50;

/** The rows one `salesorder` is read back from, as fetch queries return them. */
export interface ServerOrderRows {
  order: Record<string, unknown>;
  lines: Record<string, unknown>[];
  deliveries: Record<string, unknown>[];
}

export interface PastOrder {
  deliveries: { id: string; shipTo: string | null }[];
  lines: {
    product: string;
    productName: string;
    quantity: number;
    deliveryId: string | null;
  }[];
}

/** What Dynamics says about an order now: its number, state and status, with their labels. */
export interface OrderServerState {
  orderNumber: string | null;
  state: { value: number; label: string };
  status: { value: number; label: string };
}

/** An account's two newest orders with lines, newest first, and the quantities of those read so far. */
interface LastOrders {
  at: number;
  newest: string[];
  quantities: Map<string, Record<string, number>>;
}

export interface AccountAddress {
  /** `indskr_indskr_customeraddress_v2id`, which a delivery's ship-to stores. */
  id: string;
  label: string;
  isPrimary: boolean;
}

/**
 * Coordinator for the order capture module's master data (OMNI-60747). Owns no HTTP of its
 * own: it gates and sequences the pricing, delivery-schedule and screen bundle services so
 * the master sync has one call site. See docs/features/order-capture.md.
 */
@Injectable({ providedIn: 'root' })
export class OrderCaptureDataService {
  private lastRefreshAt = 0;
  private addressCache: {
    version: string;
    byAccount: Map<string, AccountAddress[]>;
  } | null = null;
  private readonly lastOrders = new Map<string, LastOrders>();
  private readonly accountReads = new Map<
    string,
    { at: number; read: Promise<Record<string, unknown> | null> }
  >();
  /** Bumped by every invalidation, so an answer fetched across one is not kept. */
  private lastOrdersEpoch = 0;
  private watchingSends = false;

  constructor(
    private readonly pricingDataService: PricingDataService,
    private readonly deliveryScheduleDataService: DeliveryScheduleDataService,
    private readonly appUiScreenDataService: AppUiScreenDataService,
    private readonly authenticationService: AuthenticationService,
    private readonly deviceService: DeviceService,
    private readonly disk: DiskService,
    private readonly dynamics: DynamicsClientService,
    private readonly records: AppUiRecordDataService,
  ) {}

  /** The new order flow is gated on its own feature action (OMNI-60976). */
  public get isEnabled(): boolean {
    return this.authenticationService.hasFeatureAction(
      FeatureActionsMap.NEW_ORDER_FLOW,
    );
  }

  /** True when opening the order screen would fetch the bundles again. */
  public get isScreenRefreshDue(): boolean {
    return Date.now() - this.lastRefreshAt >= SCREEN_REFRESH_INTERVAL_MS;
  }

  /**
   * Initial master-data fetch. Nothing here is account-scoped (pricing and schedules resolve
   * from position, screen config from its key), which is what makes it safe as an
   * independent task in the master sync pool. Settled independently: a tenant with pricing
   * but no schedules mapped must still get pricing, and each service records its own
   * EntitySyncInfo. See docs/features/order-capture.md for the one-call pricing model.
   */
  public async syncInitialData(loadFromDbOnly = false): Promise<void> {
    if (!this.isEnabled) return;
    if (!loadFromDbOnly && this.deviceService.isOffline) return;

    const pricingReplaced = await this.syncBundles(loadFromDbOnly);

    // Devices synced before the one-call switch still hold a document per account. Only after
    // the current projection was actually replaced: otherwise it may be the only pricing left.
    if (!loadFromDbOnly && pricingReplaced)
      await this.pricingDataService.purgeStaleProjections();
  }

  /**
   * Refresh on screen open, so an online rep captures against current data — at most once per
   * `SCREEN_REFRESH_INTERVAL_MS`, since every call downloads all three bundles in full. True when
   * new pricing was written, i.e. what the screen priced with may have changed.
   */
  public async syncForOrderScreen(
    loadFromDbOnly = false,
    force = false,
  ): Promise<boolean> {
    if (!this.isEnabled) return false;
    if (!loadFromDbOnly && !force && !this.isScreenRefreshDue) return false;
    return this.syncBundles(loadFromDbOnly);
  }

  /**
   * An account's addresses for choosing where each delivery ships, read from the store the
   * account sync already keeps (the account form's address subgrid), so it works offline.
   */
  public async getAddresses(accountId: string): Promise<AccountAddress[]> {
    // Read before the rows: a write landing meanwhile then changes the version the next call sees.
    const version = await this.storeVersion(ADDRESS_STORE_KEY);
    const cached =
      version && this.addressCache?.version === version
        ? this.addressCache.byAccount.get(accountId)
        : undefined;
    if (cached) return [...cached];
    // Large stores are chunked; older installs still hold one document.
    const rows =
      (await this.disk.retrieveFromChunks(ADDRESS_STORE_KEY)) ??
      (await this.disk.retrieve(ADDRESS_STORE_KEY))?.raw ??
      [];
    const addresses = toAccountAddresses(rows, accountId);
    if (version) {
      if (this.addressCache?.version !== version)
        this.addressCache = { version, byAccount: new Map() };
      this.addressCache.byAccount.set(accountId, addresses);
    }
    return [...addresses];
  }

  /**
   * The account's values for the columns its price-list rules name (`accountRuleColumns`), read online
   * and kept for offline: the device's accounts do not carry `address1_country` or `customertypecode`.
   * Empty when they cannot be read, so a rule naming them still does not match.
   */
  public async accountRuleValues(
    accountId: string | null | undefined,
    columns: readonly string[],
  ): Promise<Record<string, unknown>> {
    const wanted = columns.filter((column) => COLUMN_NAME.test(column));
    if (!isGuid(accountId) || !wanted.length) return {};
    const key = DB_KEY_PREFIXES.ORDER_ACCOUNT_RULE_VALUES + idOf(accountId);
    if (!this.deviceService.isOffline) {
      const read = await this.recentAccountRead(accountId, wanted);
      if (read) {
        await this.disk
          .updateOrInsert(key, () => ({ values: read }))
          .catch(() => undefined);
        return read;
      }
    }
    return (await this.disk.retrieve(key).catch(() => null))?.values ?? {};
  }

  /**
   * Each price list's maximum discount by id, as the last online sync read it: the pricing bundle
   * carries neither `indskr_maxdiscountpercentage` nor `indskr_maxdiscountamount`. A list with
   * neither is absent.
   */
  public async getPriceListCeilings(): Promise<
    Record<string, PriceListCeiling>
  > {
    const limits = await this.disk
      .retrieve(DB_KEY_PREFIXES.ORDER_PRICE_LIST_LIMITS)
      .catch(() => null);
    if (limits?.ceilings) return limits.ceilings;
    // Until this build's first sync, the percentages an earlier one kept.
    const older = await this.disk
      .retrieve(DB_KEY_PREFIXES.ORDER_PRICE_LIST_CEILINGS)
      .catch(() => null);
    return Object.fromEntries(
      Object.entries(older?.ceilings ?? {})
        .filter(([, percentage]) => typeof percentage === 'number')
        .map(([id, percentage]) => [id, { percentage: percentage as number }]),
    );
  }

  /**
   * What the account ordered last time: the bought quantity per product of its newest other order
   * that has lines, cancelled ones aside. Online only; null when the account has no such order.
   */
  public async getLastOrderQuantities(
    accountId: string,
    excludeOrderId: string | null,
  ): Promise<Record<string, number> | null> {
    if (!isGuid(accountId)) return null;
    this.watchSends();
    const epoch = this.lastOrdersEpoch;
    const account = idOf(accountId);
    let orders = this.lastOrders.get(account);
    if (!orders || Date.now() - orders.at >= LAST_ORDER_TTL_MS) {
      orders = {
        at: Date.now(),
        newest: await this.newestOrderIds(accountId),
        quantities: new Map(),
      };
      if (epoch === this.lastOrdersEpoch) this.lastOrders.set(account, orders);
    }
    const orderId = orders.newest.find((id) => id !== idOf(excludeOrderId));
    if (!orderId) return null;
    let quantities = orders.quantities.get(orderId);
    if (!quantities) {
      quantities = {};
      for (const line of (await this.getPastOrder(orderId)).lines)
        quantities[line.product] =
          (quantities[line.product] ?? 0) + line.quantity;
      if (epoch === this.lastOrdersEpoch)
        orders.quantities.set(orderId, quantities);
    }
    return { ...quantities };
  }

  /** The next last-order read for the account (every account, without one) goes to Dynamics. */
  public forgetLastOrders(accountId?: string | null): void {
    this.lastOrdersEpoch++;
    if (accountId) this.lastOrders.delete(idOf(accountId));
    else this.lastOrders.clear();
  }

  /** The number Dynamics gave an order, e.g. ORD-01234-F9Y0S4; null when it holds none. Online only. */
  public async getOrderNumber(orderId: string): Promise<string | null> {
    return (await this.getOrderState(orderId))?.orderNumber ?? null;
  }

  /** Any order, whatever its status or rep, with its lines' money and its deliveries. Online only. */
  public async fetchOrderForView(
    orderId: string,
  ): Promise<ServerOrderRows | null> {
    if (!isGuid(orderId)) return null;
    const [orders, lines, deliveries] = await Promise.all([
      this.dynamics.executeFetchQuery('salesorders', viewOrderFetch(orderId)),
      this.dynamics.executeFetchQuery(
        'salesorderdetails',
        viewLinesFetch(orderId),
      ),
      this.dynamics.executeFetchQuery(
        'indskr_orderdeliveries',
        deliveriesFetch([orderId]),
      ),
    ]);
    const order = (orders ?? [])[0] as Record<string, unknown> | undefined;
    return order
      ? { order, lines: lines ?? [], deliveries: deliveries ?? [] }
      : null;
  }

  /** An order's number, state and status as Dynamics holds them now. Online only. */
  public async getOrderState(
    orderId: string,
  ): Promise<OrderServerState | null> {
    if (!isGuid(orderId)) return null;
    const [row] = (await this.dynamics.executeFetchQuery(
      'salesorders',
      `<fetch top="1"><entity name="salesorder"><attribute name="ordernumber" /><attribute name="statecode" /><attribute name="statuscode" />` +
        `<filter><condition attribute="salesorderid" operator="eq" value="${orderId}" /></filter></entity></fetch>`,
    )) as (Record<string, unknown> | undefined)[];
    if (!row) return null;
    const formatted = (attribute: string) =>
      String(row[`${attribute}${FORMATTED}`] ?? row[attribute] ?? '');
    const orderNumber = row.ordernumber;
    return {
      orderNumber:
        typeof orderNumber === 'string' && orderNumber ? orderNumber : null,
      state: { value: Number(row.statecode), label: formatted('statecode') },
      status: { value: Number(row.statuscode), label: formatted('statuscode') },
    };
  }

  /** A past order's bought lines (rewards excluded) and its deliveries in order, for Reorder. Online only. */
  public async getPastOrder(orderId: string): Promise<PastOrder> {
    if (!isGuid(orderId))
      throw new Error(`OrderCaptureDataService: not an order id: ${orderId}`);
    const [lines, deliveries]: [
      Record<string, unknown>[],
      Record<string, unknown>[],
    ] = await Promise.all([
      this.dynamics.executeFetchQuery(
        'salesorderdetails',
        linesFetch([orderId]),
      ),
      this.dynamics.executeFetchQuery(
        'indskr_orderdeliveries',
        deliveriesFetch([orderId]),
      ),
    ]);
    // A draft's empty drop the rep dated, or one holding only free goods, has nothing to reorder.
    const used = new Set(
      (lines ?? [])
        .filter((row) => row.indskr_isfreegood !== true)
        .map((row) => row._indskr_orderdelivery_value),
    );
    return {
      deliveries: (deliveries ?? [])
        .filter(
          (row) =>
            row.indskr_isprimary === true ||
            used.has(row.indskr_orderdeliveryid),
        )
        .map((row) => ({
          id: String(row.indskr_orderdeliveryid),
          shipTo: (row._indskr_ship_value as string) ?? null,
        })),
      lines: (lines ?? [])
        .filter((row) => row.indskr_isfreegood !== true)
        .map((row) => ({
          product: String(row._productid_value ?? ''),
          productName: formattedText(row, '_productid_value'),
          quantity: Number(row.quantity) || 0,
          deliveryId: (row._indskr_orderdelivery_value as string) ?? null,
        }))
        .filter((line) => line.product && line.quantity > 0),
    };
  }

  /**
   * The rep's open orders (one, given `orderId`) with their lines and deliveries. Online only.
   * `drafts`, the screen's drafts dataset, picks the orders in place of the built-in query.
   */
  public async fetchOpenOrders({
    accountId,
    orderId,
    drafts,
  }: {
    accountId?: string | null;
    orderId?: string | null;
    drafts?: string | null;
  } = {}): Promise<{
    orders: Record<string, unknown>[];
    lines: Record<string, unknown>[];
    deliveries: Record<string, unknown>[];
  }> {
    const answered: Record<string, unknown>[] =
      (await this.dynamics.executeFetchQuery(
        'salesorders',
        drafts && !orderId
          ? pullFetchXml(drafts, accountId)
          : myOpenOrdersFetch(accountId, orderId),
      )) ?? [];
    // A dataset joining another table repeats an order once per joined row.
    const seen = new Set<string>();
    const orders = answered.filter((order) => {
      const id = idOf(order.salesorderid);
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
    const ids = orders.map((order) => idOf(order.salesorderid));
    if (!ids.length) return { orders, lines: [], deliveries: [] };
    const [lines, deliveries]: Record<string, unknown>[][] = await Promise.all([
      this.dynamics.executeFetchQuery('salesorderdetails', linesFetch(ids)),
      this.dynamics.executeFetchQuery(
        'indskr_orderdeliveries',
        deliveriesFetch(ids),
      ),
    ]);
    return { orders, lines: lines ?? [], deliveries: deliveries ?? [] };
  }

  /** Which of these orders Dynamics still holds open. */
  public async stillOpen(ids: readonly string[]): Promise<Set<string>> {
    if (!ids.length) return new Set();
    const rows: Record<string, unknown>[] =
      (await this.dynamics.executeFetchQuery(
        'salesorders',
        `<fetch><entity name="salesorder"><attribute name="salesorderid" /><filter>` +
          `<condition attribute="statecode" operator="eq" value="${SalesOrderState.Active}" />` +
          `<condition attribute="salesorderid" operator="in">${inValues(ids)}</condition>` +
          `</filter></entity></fetch>`,
      )) ?? [];
    return new Set(rows.map((row) => idOf(row.salesorderid)));
  }

  /** Every list that sets one, so it runs beside the bundle's sync rather than after it. */
  private async refreshPriceListCeilings(): Promise<void> {
    const rows: Record<string, unknown>[] | null =
      await this.dynamics.executeFetchQuery(
        'pricelevels',
        `<fetch><entity name="pricelevel"><attribute name="pricelevelid" /><attribute name="indskr_maxdiscountpercentage" />` +
          `<attribute name="indskr_maxdiscountamount" /><filter type="or">` +
          `<condition attribute="indskr_maxdiscountpercentage" operator="not-null" />` +
          `<condition attribute="indskr_maxdiscountamount" operator="not-null" /></filter></entity></fetch>`,
      );
    if (!rows) return;
    const ceilings: Record<string, PriceListCeiling> = {};
    for (const row of rows) {
      const percentage = row.indskr_maxdiscountpercentage;
      const amount = row.indskr_maxdiscountamount;
      ceilings[idOf(row.pricelevelid)] = {
        ...(typeof percentage === 'number' ? { percentage } : {}),
        ...(typeof amount === 'number' ? { amount } : {}),
      };
    }
    const percentages = Object.fromEntries(
      Object.entries(ceilings)
        .filter(([, ceiling]) => ceiling.percentage !== undefined)
        .map(([id, ceiling]) => [id, ceiling.percentage]),
    );
    await Promise.all([
      this.disk.updateOrInsert(DB_KEY_PREFIXES.ORDER_PRICE_LIST_LIMITS, () => ({
        ceilings,
      })),
      // An earlier build, run again on this device, reads percentages here.
      this.disk.updateOrInsert(
        DB_KEY_PREFIXES.ORDER_PRICE_LIST_CEILINGS,
        () => ({ ceilings: percentages }),
      ),
    ]);
  }

  private async recentAccountRead(
    accountId: string,
    columns: readonly string[],
  ): Promise<Record<string, unknown> | null> {
    const key = `${idOf(accountId)}|${columns.join(',')}`;
    const last = this.accountReads.get(key);
    if (last && Date.now() - last.at < ACCOUNT_READ_REUSE_MS) return last.read;
    const read = this.readAccount(accountId, columns).catch((error) => {
      console.warn(
        'OrderCaptureDataService: account rule columns unread',
        error,
      );
      return null;
    });
    this.accountReads.set(key, { at: Date.now(), read });
    return read;
  }

  private async readAccount(
    accountId: string,
    columns: readonly string[],
  ): Promise<Record<string, unknown> | null> {
    const attributes = columns.map(attributeTag).join('');
    const rows: Record<string, unknown>[] | null =
      await this.dynamics.executeFetchQuery(
        'accounts',
        `<fetch top="1"><entity name="account">${attributes}` +
          `<filter><condition attribute="accountid" operator="eq" value="${accountId}" /></filter></entity></fetch>`,
      );
    const row = rows?.[0];
    // A lookup comes back as `_column_value`, the raw id a rule compares with.
    return row
      ? Object.fromEntries(
          columns.map((c) => [c, row[c] ?? row[`_${c}_value`] ?? null]),
        )
      : null;
  }

  /** The account's two newest orders with lines, cancelled ones aside: whichever is open, the other is last. */
  private async newestOrderIds(accountId: string): Promise<string[]> {
    // Distinct: the inner join to the lines otherwise repeats an order once per line.
    const rows: Record<string, unknown>[] =
      (await this.dynamics.executeFetchQuery(
        'salesorders',
        `<fetch top="2" distinct="true"><entity name="salesorder"><attribute name="salesorderid" /><attribute name="createdon" />` +
          `<order attribute="createdon" descending="true" />` +
          `<filter><condition attribute="customerid" operator="eq" value="${accountId}" /><condition attribute="statecode" operator="ne" value="${SalesOrderState.Canceled}" /></filter>` +
          `<link-entity name="salesorderdetail" from="salesorderid" to="salesorderid" link-type="inner" /></entity></fetch>`,
      )) ?? [];
    return rows.map((row) => idOf(row.salesorderid)).filter(Boolean);
  }

  /** From the first last-order read: any answer to a Save, Submit or Scrap may change it. */
  private watchSends(): void {
    if (this.watchingSends) return;
    this.watchingSends = true;
    this.records.settled$
      .pipe(filter((settled) => settled.record.entity === ORDER_SCREEN_ENTITY))
      .subscribe((settled) => this.forgetLastOrders(settled.record.parentId));
  }

  /** The revisions of every document under `key`, from the index alone: any write changes them. */
  private async storeVersion(key: string): Promise<string | null> {
    const index = (await this.disk
      .batchFetch(
        {
          // eslint-disable-next-line @typescript-eslint/naming-convention -- PouchDB's option
          include_docs: false,
          startkey: key,
          endkey: `${key}${PREFIX_SEARCH_ENDKEY_UNICODE}`,
        },
        false,
      )
      .catch(() => null)) as {
      rows?: { id?: string; value?: { rev?: string } }[];
    } | null;
    const rows = index?.rows;
    return rows?.length
      ? rows.map((row) => `${row.id}@${row.value?.rev}`).join()
      : null;
  }

  /** Returns whether the pricing bundle was replaced. */
  private async syncBundles(loadFromDbOnly: boolean): Promise<boolean> {
    const [pricing, , , ceilings] = await Promise.allSettled([
      this.pricingDataService.syncPricingBundle(
        null,
        'all',
        undefined,
        loadFromDbOnly,
      ),
      this.deliveryScheduleDataService.syncDeliveryScheduleBundle(
        loadFromDbOnly,
      ),
      this.appUiScreenDataService.syncScreenBundle(
        ORDER_SCREEN_KEY,
        ORDER_SCREEN_ENTITY,
        loadFromDbOnly,
      ),
      loadFromDbOnly || this.deviceService.isOffline
        ? undefined
        : this.refreshPriceListCeilings(),
    ]);
    if (ceilings.status === 'rejected')
      console.warn(
        'OrderCaptureDataService: price list ceilings',
        ceilings.reason,
      );
    const replaced = pricing.status === 'fulfilled' && pricing.value === true;
    if (replaced) this.lastRefreshAt = Date.now();
    return replaced;
  }
}

/** Active addresses of one account, primary first. */
export function toAccountAddresses(
  rows: readonly Record<string, unknown>[],
  accountId: string,
): AccountAddress[] {
  const byId = new Map<string, AccountAddress>();
  for (const row of rows ?? []) {
    const id = String(row?.[`${ADDRESS_LINK}.${ADDRESS_LINK}id`] ?? '');
    if (
      !id ||
      row.accountid !== accountId ||
      Number(row[`${ADDRESS_LINK}.statecode`] ?? 0) !== 0
    )
      continue;
    const label = String(
      row['indskr_address.indskr_composite'] ||
        row[`${ADDRESS_LINK}.indskr_name`] ||
        row[`${ADDRESS_LINK}.indskr_address_Formatted`] ||
        '',
    );
    byId.set(id, {
      id,
      label,
      isPrimary: row[`${ADDRESS_LINK}.indskr_isprimary`] === true,
    });
  }
  return [...byId.values()].sort(
    (a, b) => Number(b.isPrimary) - Number(a.isPrimary),
  );
}

/**
 * The rep's own open orders, newest first; `eq-userid` is the calling user, so no id is sent.
 * SaveOrder writes as SYSTEM, so its orders name the rep in `createdonbehalfby`, not `createdby`.
 */
function myOpenOrdersFetch(
  accountId?: string | null,
  orderId?: string | null,
): string {
  const account =
    (isGuid(accountId)
      ? `<condition attribute="customerid" operator="eq" value="${accountId}" />`
      : '') +
    (isGuid(orderId)
      ? `<condition attribute="salesorderid" operator="eq" value="${orderId}" />`
      : '');
  return (
    `<fetch top="${PULL_LIMIT}"><entity name="salesorder"><all-attributes />${CURRENCY_LINK}` +
    `<filter><condition attribute="statecode" operator="eq" value="${SalesOrderState.Active}" />${account}` +
    `${MY_ORDERS}</filter>` +
    `<order attribute="modifiedon" descending="true" /></entity></fetch>`
  );
}

/** An order query whose rows carry their currency's code (`CURRENCY_CODE`). Aggregates are left as they are. */
export function withCurrencyCode(fetchXml: string): string {
  const linked = new RegExp(`alias=["']${CURRENCY_ALIAS}["']`);
  if (linked.test(fetchXml) || /aggregate=["']true["']/.test(fetchXml))
    return fetchXml;
  return addToRootEntity(fetchXml, CURRENCY_LINK);
}

/** Created by the rep, or by SaveOrder (as SYSTEM) on their behalf. */
export const MY_ORDERS =
  '<filter type="or"><condition attribute="createdby" operator="eq-userid" />' +
  '<condition attribute="createdonbehalfby" operator="eq-userid" /></filter>';

/**
 * The screen's drafts dataset as the pull runs it: every column (the form decides which header
 * fields a draft keeps, and the schedule's columns are copied whole), only the rep's own orders
 * whatever the dataset says (a pulled draft is theirs to edit), and the account in place of the
 * record tokens a pull has no record to fill.
 */
export function pullFetchXml(
  drafts: string,
  accountId?: string | null,
): string {
  let fetchXml = withCurrencyCode(
    addFilter(selectAll(withoutTokens(drafts)), MY_ORDERS),
  );
  if (isGuid(accountId))
    fetchXml = addCondition(fetchXml, 'customerid', accountId);
  return setTop(fetchXml, PULL_LIMIT);
}

function attributeTag(column: string): string {
  return `<attribute name="${column}" />`;
}

function inValues(ids: readonly string[]): string {
  return ids
    .filter((id) => isGuid(id))
    .map((id) => `<value>${id}</value>`)
    .join('');
}

function linesFetch(orderIds: readonly string[]): string {
  return (
    `<fetch><entity name="salesorderdetail"><attribute name="salesorderdetailid" /><attribute name="salesorderid" />` +
    `<attribute name="productid" /><attribute name="quantity" /><attribute name="indskr_isfreegood" />` +
    `<attribute name="indskr_orderdelivery" /><attribute name="lineitemnumber" />` +
    `<filter><condition attribute="salesorderid" operator="in">${inValues(orderIds)}</condition></filter></entity></fetch>`
  );
}

function viewOrderFetch(orderId: string): string {
  return (
    `<fetch top="1"><entity name="salesorder"><all-attributes />` +
    `<filter><condition attribute="salesorderid" operator="eq" value="${orderId}" /></filter>` +
    `<link-entity name="transactioncurrency" from="transactioncurrencyid" to="transactioncurrencyid" link-type="outer" alias="currency">` +
    `<attribute name="isocurrencycode" /><attribute name="currencyprecision" /></link-entity></entity></fetch>`
  );
}

/** As `linesFetch`, with each line's money and its product's number, for a read-only view. */
function viewLinesFetch(orderId: string): string {
  return (
    `<fetch><entity name="salesorderdetail"><attribute name="salesorderdetailid" /><attribute name="productid" />` +
    `<attribute name="quantity" /><attribute name="indskr_isfreegood" /><attribute name="indskr_orderdelivery" />` +
    `<attribute name="priceperunit" /><attribute name="baseamount" /><attribute name="extendedamount" />` +
    `<filter><condition attribute="salesorderid" operator="eq" value="${orderId}" /></filter>` +
    `<link-entity name="product" from="productid" to="productid" link-type="outer" alias="product">` +
    `<attribute name="productnumber" /></link-entity></entity></fetch>`
  );
}

function deliveriesFetch(orderIds: readonly string[]): string {
  return (
    `<fetch><entity name="indskr_orderdelivery"><attribute name="indskr_orderdeliveryid" /><attribute name="indskr_salesorder" />` +
    `<attribute name="indskr_sequence" /><attribute name="indskr_deliverydate" /><attribute name="indskr_ship" /><attribute name="indskr_isprimary" />` +
    `<order attribute="indskr_sequence" /><filter><condition attribute="indskr_salesorder" operator="in">${inValues(orderIds)}</condition></filter></entity></fetch>`
  );
}
